import _get from 'lodash/get';
import { createSelector } from 'reselect';

import {
  IIndicativeCondition,
  IIndicativeOffer,
  IInquiryDetails,
} from 'models/InquiryDetails/DefaultInquiryDetails.model';
import { ITransparencyRegister } from 'models/types/TransparencyRegister.types';
import {
  EXTERNAL_REFERENCE_ID,
  PRODUCT_KIND__PROPERTY_LOAN,
  PROJECT_FINANCING__OBJECT_USAGE,
  PROPERTY_LOAN_KIND__CLASSIC,
} from 'modules/Inquiry/Form/formFields';
import { INQUIRY_SECTIONS } from 'modules/InquiryDetails/InquirySections.model';
import { CustomerConfirmationStatus } from 'pages/customerPortal/InquiryDetails/Dashboard/ConfirmationStatus/constants';

import { OFFER_STATE, OFFER_STATUS } from '../../modules/Offers/InquiryOffersSection/constants';
import { IState } from '../index';

export const getInquiryTypeSpecificDetailsSelector = (state: IState) =>
  state.inquiryDetails?.details?.inquiryTypeSpecificDetails;

export const getInquiryDetailsSelector = (state: IState) => state.inquiryDetails?.details;
export const getInquiryDetailsEditSelector = (state: IState) => state?.inquiryDetails?.editDetails;
export const getCompeonOffersSelector = (state: IState) => state.inquiryDetails?.compeonOffers;
export const getBankOffersSelector = (state: IState) => state.inquiryDetails?.bankOffers;
export const getVideIdentificationsDetails = (state: IState) => state.inquiryDetails?.details;

export const getInquiryFormTypeSelector = (state: IState) =>
  state.inquiryDetails?.details?.formType;

export const getInquiryIdSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && details.inquiryId,
);

export const getBeneficialOwnerSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails)?.beneficialOwner,
);

export const getAllowDigitalProcessingSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).allowDigitalProcessing,
);

export const getLegalRepresentativesRequireManualMerging = createSelector(
  getInquiryDetailsSelector,
  (details): any =>
    details && (details as IInquiryDetails).legalRepresentativesRequireManualMerging,
);

export const getCustomerLegalRepresentativeId = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).customerLegalRepresentativeId,
);

export const getlegalRepresentativesAccepted = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).legalRepresentativesAccepted,
);

export const getKycCompanies = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).kycCompanies,
);

export const getCustomersSoleSignatureAuthorized = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).customersSoleSignatureAuthorized,
);

export const getVideoIdentificationFile = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).videoIdentification,
);

export const getVideoIdentificationsDetails = createSelector(
  getInquiryDetailsSelector,
  (details) => details && details.videoIdentificationsDetail,
);

export const getInquiryStatusSelector = createSelector(
  getInquiryDetailsSelector,
  (details) => details && details.status,
);

export const getInquirySubStatusSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && details.substatus,
);

export const getInquiryCreatedAtSelector = createSelector(
  getInquiryDetailsSelector,
  (details) => details && details.createdAt,
);

export const getInquiryCollateralsSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).collaterals,
);

export const getInquiryProjectFinancingTypeOfUse = createSelector(
  getInquiryDetailsSelector,
  (details): any =>
    _get(details, [INQUIRY_SECTIONS.FINANCING, PROJECT_FINANCING__OBJECT_USAGE], []),
);

export const getInquiryPurposeKind = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'financingPurposeFields.purpose-kind'),
);
export const getInquiryProductKind = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'inquiryDetailFields.product-kind'),
);
export const getInquiryWasMovedToNonFastlane = createSelector(
  getInquiryDetailsSelector,
  (details): any => _get(details, 'fastlaneCancelledDate'),
);
export const getInquiryAdditionalFields = createSelector(
  getInquiryDetailsSelector,
  (details): any => _get(details, 'additionalFields'),
);

export const getInquiryCustomerName = createSelector(getInquiryDetailsSelector, (details): any => {
  const customerFirstName = _get(details, 'userProfileFields.company-details-first-name');
  const customerLastName = _get(details, 'userProfileFields.company-details-last-name');
  const customerFullName = `${customerFirstName} ${customerLastName}`;
  return { customerFirstName, customerLastName, customerFullName };
});

export const getCustomerConfirmationStatus = createSelector(
  getInquiryDetailsSelector,
  (details): any => {
    const isCustomerStatusConfirmed = _get(details, 'dashboardActions.customerInvitationAccepted');
    return isCustomerStatusConfirmed
      ? CustomerConfirmationStatus.CONFIRMED
      : CustomerConfirmationStatus.CONFIRMATION_PENDING;
  },
);

export const getInquiryLane = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'lane'),
);

export const getInquiryAllRequiredFilesUploaded = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).allRequiredFilesUploaded,
);

// FIXME this is incorrectly typed
// @ts-ignore
export const getCompanyId = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'company.id'),
);

export const hasAcceptedCompeonOfferSelector = createSelector(getCompeonOffersSelector, (offers) =>
  offers.some(
    (offer) => offer.status === OFFER_STATUS.ACCEPTED && offer.state !== OFFER_STATE.UNAVAILABLE,
  ),
);
export const hasShownInterestInCompeonOfferSelector = createSelector(
  getCompeonOffersSelector,
  (offers) =>
    offers.some((offer) => offer.hasShownInterest && offer.state !== OFFER_STATE.UNAVAILABLE),
);
export const hasAcceptedBankOfferSelector = createSelector(getBankOffersSelector, (offers) =>
  offers.some((offer) => offer.status === OFFER_STATUS.ACCEPTED),
);

export const hasOffersSelector = createSelector(
  [getCompeonOffersSelector, getBankOffersSelector],
  (compeonOffers, bankOffers) => !!compeonOffers.length || !!bankOffers.length,
);

export const inquiryDetailsCustomerSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).customer,
);

// FIXME this is incorrectly typed
export const inquiryDetailsCustomerIdSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => _get(details, 'customer.id'),
);

export const inquiryDetailsBankAdvisorSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails).bankAdvisor,
);

export const isPropertyLoanClassic = createSelector(
  getInquiryDetailsSelector,
  (details): any =>
    _get(details, 'inquiryDetailFields.property-loan-kind') === PROPERTY_LOAN_KIND__CLASSIC,
);

export const isProductPropertyLoanKind = createSelector(
  getInquiryDetailsSelector,
  (details): any =>
    _get(details, 'inquiryDetailFields.product-kind') === PRODUCT_KIND__PROPERTY_LOAN,
);

export const getInquiryFavoredDecisionDate = createSelector(
  getInquiryDetailsSelector,
  (details): any => _get(details, 'inquiryDetailFields.favored-decision-date'),
);

export const loanTermsInMonthExists = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'inquiryDetailFields.property-loan-term-in-months'),
);
// FIXME this is incorrectly typed
export const isValidForCompeon = createSelector(
  [isPropertyLoanClassic, getInquiryFavoredDecisionDate, loanTermsInMonthExists],
  (possibleLoanType, hasFavoredDate, hasLoanMonth) =>
    possibleLoanType && hasFavoredDate && hasLoanMonth,
);

// FIXME this is incorrectly typed
export const getInquiryCustomerSelector = createSelector(
  getInquiryDetailsSelector,
  (details): any => (details as IInquiryDetails)?.customer,
);

export const getKfwProgram = (state: IState) => state.inquiryDetails.kfwProgram;
export const getFastLane = (state: IState) => state.inquiryDetails.fastLane;
export const getInconsistencyReportUrl = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.inconsistencyReportUrl;
export const getIndicativeConditionCompleted = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.indicativeConditionCompleted;
export const getIndicativeOffer = (state: IState): Partial<IIndicativeOffer> | undefined =>
  state.inquiryDetails.details?.dashboardActions?.indicativeOffer;
export const getIndicativeCondition = (state: IState): Partial<IIndicativeCondition> | undefined =>
  state.inquiryDetails.details?.dashboardActions?.indicativeCondition;

export const getTransparencyResults = (state: IState): Partial<ITransparencyRegister> | undefined =>
  state.inquiryDetails.details?.dashboardActions?.transparencyRegister;

export const getCompeonInquiryId = (state: IState) => state.inquiryDetails.externalId;
// FIXME this is incorrectly typed
export const getExternalReference = createSelector(
  getInquiryDetailsSelector,
  (details): any => details && (details as IInquiryDetails)[EXTERNAL_REFERENCE_ID],
);
export const getInquiryCompeonStatus = (state: IState) => state.inquiryDetails.compeonStatus;
export const isKycCompleted = (state: IState) =>
  state.inquiryDetails.details?.inquiryTypeSpecificDetails?.kycProcessCompleted;
export const getKycStartedAt = (state: IState) =>
  state.inquiryDetails.details?.inquiryTypeSpecificDetails?.kycProcessStartedAt;
export const isKycFailed = (state: IState) =>
  state.inquiryDetails.details?.inquiryTypeSpecificDetails?.kycProcessFailed;
export const isKycSkipped = (state: IState) =>
  state.inquiryDetails.details?.inquiryTypeSpecificDetails?.kycProcessSkipped;
export const isCustomerInvited = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.customerInvitationSend;
export const isCustomerInvitationAccepted = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.customerInvitationAccepted;

export const manualCrefoReportFound = (state: IState) =>
  state.inquiryDetails.details?.inquiryTypeSpecificDetails?.manualCrefoReportFound;

export const getIndicativeOfferCalculations = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.indicativeOfferCalculations;

export const getLegalRepresentatives = (state: IState) =>
  state.inquiryDetails.details ? state.inquiryDetails.details.legalRepresentatives : [];

export const getContractDetails = (store: IState) =>
  (store?.inquiryDetails?.details as IInquiryDetails).contractDetails;

export const getCustomerData = (store: IState) =>
  (store?.inquiryDetails?.details as IInquiryDetails).customerData;

export const getCompanyData = (store: IState) =>
  (store?.inquiryDetails?.details as IInquiryDetails)?.company;

export const getCompeonDocument = (store: IState) =>
  (store?.inquiryDetails?.details as IInquiryDetails)?.compeonDocument;

export const getCompeonInquiryState = (store: IState) =>
  (store?.inquiryDetails?.details as IInquiryDetails)?.compeonState;

export const isMarketDataSent = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.marketDataSent;

export const assignedRealEstateExpert = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.assignedRealEstateExpert;

export const getIsQuickCheckStarted = (state: IState) =>
  state.inquiryDetails.details?.dashboardActions?.isQuickCheckStarted;
// FIXME this is incorrectly typed
export const getProjectFinancingRole = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'financingPurposeFields.project-financing-role'),
);
// FIXME this is incorrectly typed
export const hasOnlyFutureUsage = createSelector(getInquiryDetailsSelector, (details): any => {
  return !_get(details, 'financingPurposeFields.project-financing-usage-deviation');
});
// FIXME this is incorrectly typed
export const getPayoutTime = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'inquiryDetailFields.purchased-invoices-paid-out-time'),
);
// FIXME this is incorrectly typed
export const getPrefinancingPeriod = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'inquiryDetailFields.pre-financing-period'),
);
// FIXME this is incorrectly typed
export const getBfssLane = createSelector(getInquiryDetailsSelector, (details): any =>
  _get(details, 'bfssLane'),
);
// FIXME this is incorrectly typed
export const getBeneficiaryOwners = (state: IState) =>
  state.inquiryDetails.details ? (state.inquiryDetails.details as any).beneficiaryOwners : [];
// FIXME this is incorrectly typed
export const getFinancingPurposeIndustryType = createSelector(
  getInquiryDetailsSelector,
  (details): any => {
    return _get(details, 'financingPurposeFields.industry');
  },
);
